import { useState } from 'react';
import { FONTS } from '../styles/tokens';

interface AppIconProps {
  /** Display name of the app (used for the fallback initial) */
  name: string;
  /** Primary icon URL, from the Keycloak `vertex.icon` attribute. Used in light
   *  theme, and in dark theme when no dark variant has been uploaded. */
  icon: string;
  /** Optional dark-theme icon, from `vertex.icon.dark`. When absent the primary
   *  mark is used as-is. */
  iconDark?: string;
  /** Whether dark mode is active */
  isDark: boolean;
  /** Rendered square size in px (default 56) */
  size?: number;
}

/** Number of hue buckets for deterministic avatar colors */
const HUE_BUCKETS = 12;

/** Corner radius as a fraction of size. Kept modest so tiles read as
 *  rounded squares, not squircles. */
const RADIUS_RATIO = 0.08;

/**
 * Derive a deterministic HSL hue from an app name.
 * Uses a simple char-code sum mapped to evenly-spaced hue buckets
 * so each app gets a visually distinct color.
 */
function nameToHue(name: string): number {
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  const bucket = sum % HUE_BUCKETS;
  return Math.round((bucket / HUE_BUCKETS) * 360);
}

/**
 * App icon with automatic fallback.
 *
 * Uploaded marks fill the tile and bring their own background. A white plate
 * around them read as a border on dark logos (Cadence) and as a second frame
 * on light ones, so the plate is gone. Letter fallback still paints a colored
 * rounded square.
 */
export function AppIcon({ name, icon, iconDark, isDark, size = 56 }: AppIconProps): React.JSX.Element {
  const [imgFailed, setImgFailed] = useState(false);
  const resolved = isDark && iconDark ? iconDark : icon;
  const showFallback = !resolved || imgFailed;
  const radius = Math.max(4, Math.round(size * RADIUS_RATIO));

  if (showFallback) {
    const hue = nameToHue(name);
    const initial = (name.charAt(0) || '?').toUpperCase();
    return (
      <div
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: `${radius}px`,
          backgroundColor: `hsl(${hue}, 55%, ${isDark ? 35 : 50}%)`,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: FONTS.heading,
            fontSize: `${Math.round(size * 0.43)}px`,
            fontWeight: 600,
            color: '#FFFFFF',
            lineHeight: 1,
          }}
        >
          {initial}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${radius}px`,
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: 'transparent',
      }}
    >
      {/* Explicit px (not %) so iOS Safari cannot collapse the mark to 0.
          alt is empty because every call site already shows the app name. */}
      <img
        src={resolved}
        alt=""
        width={size}
        height={size}
        onError={() => setImgFailed(true)}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          maxWidth: 'none',
          maxHeight: 'none',
          objectFit: 'contain',
          display: 'block',
        }}
      />
    </div>
  );
}
